import { useEffect, useState } from 'react';
import {
  Container, Typography, Box, Paper, Button, Stack, TextField, Alert, Snackbar,
  CircularProgress, Divider, Chip, InputAdornment, IconButton, Switch, FormControlLabel,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { getJoinCode, rotateJoinCode, getAssessorName, setAssessorName } from '../../api/saasApi';
import { useMe } from '../../context/MeContext';

/**
 * Clinic-level settings. The join code is how colleagues request membership —
 * anyone holding it can create an account that lands in the approval queue,
 * so rotating it is the way to cut off a code that has spread too far.
 */
const ClinicSettings = () => {
  const { me, isOwner } = useMe();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(true);
  const [rotating, setRotating] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');

  // Report assessor-name setting: off = show the assessing clinician's name,
  // on = print a single fixed name (required when on) on every report.
  const [assessorMode, setAssessorMode] = useState(false);
  const [assessorName, setAssessorNameValue] = useState('');
  const [assessorLoading, setAssessorLoading] = useState(true);
  const [assessorSaving, setAssessorSaving] = useState(false);
  const [assessorError, setAssessorError] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const res = await getJoinCode();
        if (res.data?.succeeded === false) setError(res.data.messages?.[0] || 'Could not load the clinic code.');
        else setCode(res.data?.data || '');
      } catch (e) {
        setError('Could not load the clinic code.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await getAssessorName();
        const s = res.data?.data;
        if (s) {
          setAssessorMode(!!s.useStaticAssessorName);
          setAssessorNameValue(s.staticAssessorName || '');
        }
      } catch (e) {
        // Non-fatal — leave the defaults (dynamic mode).
      } finally {
        setAssessorLoading(false);
      }
    })();
  }, []);

  const saveAssessor = async () => {
    setAssessorError('');
    if (assessorMode && !assessorName.trim()) {
      setAssessorError('Enter the assessor name to display, or turn the setting off.');
      return;
    }
    setAssessorSaving(true);
    try {
      const res = await setAssessorName({
        useStaticAssessorName: assessorMode,
        staticAssessorName: assessorMode ? assessorName.trim() : null,
      });
      if (res.data?.succeeded === false) {
        setAssessorError(res.data.messages?.[0] || 'Could not save the setting.');
        return;
      }
      setToast('Assessor name setting saved.');
    } catch (e) {
      setAssessorError('Could not save the setting.');
    } finally {
      setAssessorSaving(false);
    }
  };

  const rotate = async () => {
    setRotating(true);
    setError('');
    try {
      const res = await rotateJoinCode();
      if (res.data?.succeeded === false) {
        setError(res.data.messages?.[0] || 'Could not issue a new code.');
        return;
      }
      setCode(res.data?.data || '');
      setToast('New clinic code issued. The old one no longer works.');
    } catch (e) {
      setError('Could not issue a new code.');
    } finally {
      setRotating(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setToast('Clinic code copied.');
    } catch (e) {
      setError('Could not copy — select the code and copy it manually.');
    }
  };

  if (loading) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const billing = me?.billing;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Clinic settings</Typography>
      {error && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
          <Typography variant="h6" fontWeight={700}>{me?.tenant?.name || 'Your clinic'}</Typography>
          {me?.tenant?.status && <Chip size="small" label={me.tenant.status} />}
          {isOwner && <Chip size="small" color="primary" label="You are the owner" />}
        </Stack>
        {billing && !billing.isExempt && (
          <Typography variant="body2" color="text.secondary">
            Assessment price: {billing.currency || 'INR'} {billing.assessmentPrice}
          </Typography>
        )}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Clinic code</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Share this with colleagues so they can request to join. Every request still needs your
          approval on the Clinicians page — the code alone grants no access.
        </Typography>

        <TextField
          value={code}
          fullWidth
          InputProps={{
            readOnly: true,
            sx: { fontFamily: 'monospace', fontSize: 22, letterSpacing: 4, fontWeight: 700 },
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={copy} aria-label="Copy clinic code"><ContentCopyIcon /></IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Divider sx={{ my: 3 }} />

        <Typography variant="body2" color="text.secondary" mb={1}>
          If the code has been shared too widely, issue a new one. Anyone still holding the old code
          will no longer be able to request access. Approved clinicians are unaffected.
        </Typography>
        <Button variant="outlined" color="warning" onClick={rotate} disabled={rotating}>
          {rotating ? 'Issuing…' : 'Issue a new code'}
        </Button>
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" fontWeight={700} gutterBottom>Report assessor name</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          By default each report shows the clinician who performed the assessment.
          Turn this on to print a single fixed name on every report instead.
        </Typography>

        {assessorError && (
          <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAssessorError('')}>{assessorError}</Alert>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={assessorMode}
              onChange={(e) => setAssessorMode(e.target.checked)}
              disabled={assessorLoading}
            />
          }
          label="Use a fixed assessor name on all reports"
        />

        {assessorMode && (
          <TextField
            label="Assessor name"
            value={assessorName}
            onChange={(e) => setAssessorNameValue(e.target.value)}
            fullWidth
            required
            sx={{ mt: 2 }}
            placeholder="e.g. Dr. Praveen Suman"
          />
        )}

        <Box sx={{ mt: 2 }}>
          <Button variant="contained" onClick={saveAssessor} disabled={assessorSaving || assessorLoading}>
            {assessorSaving ? 'Saving…' : 'Save'}
          </Button>
        </Box>
      </Paper>

      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
    </Container>
  );
};

export default ClinicSettings;
